import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { signIn, confirmSignIn, resetPassword, confirmResetPassword, getCurrentUser, signOut, fetchAuthSession } from 'aws-amplify/auth';

type AuthView = 'signIn' | 'newPasswordRequired' | 'forgotPassword' | 'confirmResetPassword';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './login.page.html',
})
export class LoginPageComponent implements OnInit {
  private router = inject(Router);

  // Current view state
  currentView: AuthView = 'signIn';

  // Sign In form
  email = '';
  password = '';

  // New Password form (first login)
  newPassword = '';
  confirmNewPassword = '';

  // Forgot Password form
  resetEmail = '';
  resetCode = '';
  resetNewPassword = '';
  confirmResetNewPassword = '';

  // UI State
  loading = false;
  error = '';
  successMessage = '';
  showPassword = false;
  showNewPassword = false;

  async ngOnInit() {
    // Check if already authenticated and if token is still valid
    try {
      const user = await getCurrentUser();
      console.log('Current user:', user);

      if (user) {
        // User exists, but check if token is still valid
        try {
          const session = await fetchAuthSession();
          if (session.tokens) {
            // Token is valid, redirect to projects
            console.log('Valid session found, redirecting to projects...');
            this.router.navigate(['/projects']);
          } else {
            // No valid tokens, sign out
            console.log('No valid tokens, signing out...');
            await signOut();
            this.error = 'Your session has expired. Please sign in again.';
          }
        } catch (sessionError: any) {
          // Token is expired or invalid, sign out the user
          console.log('Session error (possibly expired token), signing out:', sessionError);
          await signOut();
          this.error = 'Your session has expired. Please sign in again.';
        }
      }
    } catch (error) {
      // Not authenticated or error, stay on login page
      console.log('No authenticated user or error:', error);
    }
  }

  async onSignIn() {
    if (!this.email || !this.password) {
      this.error = 'Please enter your email and password';
      return;
    }

    this.loading = true;
    this.error = '';

    try {
      const result = await signIn({
        username: this.email,
        password: this.password,
      });

      if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        // First login - need to change password
        this.currentView = 'newPasswordRequired';
        this.loading = false;
        return;
      }

      if (result.isSignedIn) {
        this.router.navigate(['/projects']);
      }
    } catch (err: any) {
      console.error('Sign in error:', err);
      this.error = this.getErrorMessage(err);
    } finally {
      this.loading = false;
    }
  }

  async onConfirmNewPassword() {
    if (!this.newPassword || !this.confirmNewPassword) {
      this.error = 'Please enter and confirm your new password';
      return;
    }

    if (this.newPassword !== this.confirmNewPassword) {
      this.error = 'Passwords do not match';
      return;
    }

    if (this.newPassword.length < 8) {
      this.error = 'Password must be at least 8 characters';
      return;
    }

    this.loading = true;
    this.error = '';

    try {
      const result = await confirmSignIn({
        challengeResponse: this.newPassword,
      });

      if (result.isSignedIn) {
        this.router.navigate(['/projects']);
      }
    } catch (err: any) {
      console.error('Confirm new password error:', err);
      this.error = this.getErrorMessage(err);
    } finally {
      this.loading = false;
    }
  }

  async onForgotPassword() {
    if (!this.resetEmail) {
      this.error = 'Please enter your email address';
      return;
    }

    this.loading = true;
    this.error = '';

    try {
      await resetPassword({ username: this.resetEmail });
      this.currentView = 'confirmResetPassword';
      this.successMessage = 'A verification code has been sent to your email';
    } catch (err: any) {
      console.error('Reset password error:', err);
      this.error = this.getErrorMessage(err);
    } finally {
      this.loading = false;
    }
  }

  async onConfirmResetPassword() {
    if (!this.resetCode || !this.resetNewPassword || !this.confirmResetNewPassword) {
      this.error = 'Please fill in all fields';
      return;
    }

    if (this.resetNewPassword !== this.confirmResetNewPassword) {
      this.error = 'Passwords do not match';
      return;
    }

    if (this.resetNewPassword.length < 8) {
      this.error = 'Password must be at least 8 characters';
      return;
    }

    this.loading = true;
    this.error = '';

    try {
      await confirmResetPassword({
        username: this.resetEmail,
        confirmationCode: this.resetCode,
        newPassword: this.resetNewPassword,
      });

      this.successMessage = 'Password reset successful! You can now sign in with your new password.';
      this.resetForm();
      this.currentView = 'signIn';
    } catch (err: any) {
      console.error('Confirm reset password error:', err);
      this.error = this.getErrorMessage(err);
    } finally {
      this.loading = false;
    }
  }

  goToForgotPassword() {
    this.resetForm();
    this.resetEmail = this.email; // Pre-fill with email if already entered
    this.currentView = 'forgotPassword';
  }

  goToSignIn() {
    this.resetForm();
    this.currentView = 'signIn';
  }

  private resetForm() {
    this.error = '';
    this.successMessage = '';
    this.password = '';
    this.newPassword = '';
    this.confirmNewPassword = '';
    this.resetCode = '';
    this.resetNewPassword = '';
    this.confirmResetNewPassword = '';
  }

  private getErrorMessage(err: any): string {
    const message = err?.message || err?.name || 'An error occurred';

    // Map common Cognito errors to user-friendly messages
    if (message.includes('Incorrect username or password')) {
      return 'Incorrect email or password. Please try again.';
    }
    if (message.includes('User does not exist')) {
      return 'No account found with this email address.';
    }
    if (message.includes('Password did not conform')) {
      return 'Password must include uppercase, lowercase, number, and special character.';
    }
    if (message.includes('Invalid verification code')) {
      return 'Invalid verification code. Please check and try again.';
    }
    if (message.includes('Attempt limit exceeded')) {
      return 'Too many attempts. Please try again later.';
    }
    if (message.includes('User is disabled')) {
      return 'This account has been disabled. Please contact your administrator.';
    }

    return message;
  }

  togglePasswordVisibility(field: 'password' | 'newPassword') {
    if (field === 'password') {
      this.showPassword = !this.showPassword;
    } else {
      this.showNewPassword = !this.showNewPassword;
    }
  }
}
